import React, { useRef, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import CustomModal from "../../Common/CustomModal";
import { FormControlLabel, useMediaQuery, useTheme } from "@mui/material";
import { CustomInput } from "../../Common/CustomInputs";
import CustomSlider from "../../Common/CustomSlider";
import CustomDatePicker from "../../Common/CustomDatePicker";
import { InfoOutlined, TaskAltOutlined } from "@mui/icons-material";
import axiosInstance from "../../../utils/config/config";
import { getUserById } from "../../../redux/actions/user.dispatcher";
import {
  MODAL_TITLE,
  SAVE_BTN_TEXT,
  CANCEL_BTN_TEXT,
  SUBTITLE,
  TOGGLE_LABEL,
  SLIDER_LABELS,
  INPUT_LABELS,
  RESULT_LABELS,
  DISCLAIMER_TEXT,
  ERROR_MESSAGES,
  SLIDER_CONFIG,
  RATE_INPUT_CONFIG,
} from "./CalculatorModal.constants";
import { validateForm } from "./CalculatorModal.utils";
import {
  ContentBox,
  Subtitle,
  ToggleLabel,
  GridContainer,
  DateLabel,
  DateHint,
  DisclaimerStack,
  DisclaimerText,
  ResultBox,
  ResultLabel,
  ResultAmount,
  ResultValue,
  ResultSuffix,
  checkboxIconSx,
  checkboxCheckedIconSx,
  CheckboxIcon,
  InputWrapper,
} from "./CalculatorModal.styles";
import {
  calculateWeeklyIncome,
  calculateAnnualIncome,
} from "./CalculatorModal.utils";
import { colors } from "../../../theme/theme";

const CalculatorModal = ({
  open,
  handleClose,
  setLoading,
  setSuccess,
  setErrorState,
}) => {
  const resultRef = useRef(null);
  const dispatch = useDispatch();
  const { userData } = useSelector((state) => state.user);
  const theme = useTheme();
  const isMediumScreen = useMediaQuery(theme.breakpoints.down(900));

  const [totalIncomeToggle, setTotalIncomeToggle] = useState(
    userData?.showTotalIncome || false
  );
  const [rate, setRate] = useState(userData?.ratePerClient || 0);
  const [desiredClients, setDesiredClients] = useState(userData?.clients || 1);
  const [sessionLength, setSessionLength] = useState(
    userData?.sessionLength || 3
  );
  const [startDate, setStartDate] = useState(
    userData?.startDate ? dayjs(userData?.startDate) : null
  );

  useEffect(() => {
    if (open && userData) {
      setTotalIncomeToggle(userData?.showTotalIncome || false);
      setRate(userData?.ratePerClient || 0);
      setDesiredClients(userData?.clients || 1);
      setSessionLength(userData?.sessionLength || 3);
      setStartDate(userData?.startDate ? dayjs(userData?.startDate) : null);
    }
  }, [open, userData]);

  const scrollToResult = () => {
    resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleSave = async () => {
    scrollToResult();
    setErrorState({ error: false, message: "" });

    const validation = validateForm(
      startDate,
      rate,
      desiredClients,
      ERROR_MESSAGES
    );
    if (!validation.valid) {
      setErrorState({ error: true, message: validation.message });
      return;
    }

    try {
      setLoading(true);

      const response = await axiosInstance.put("user/update", {
        ratePerClient: rate,
        clients: desiredClients,
        startDate: startDate,
        sessionLength: sessionLength,
        showTotalIncome: totalIncomeToggle,
      });

      if (response.status === 200) {
        setLoading(false);
        setSuccess(true);
        setErrorState({ error: false, message: "" });
        handleClose();
      }
    } catch (error) {
      console.error(error);
      setErrorState({
        error: true,
        message: ERROR_MESSAGES.SAVE_FAILED,
      });
    } finally {
      setLoading(false);
      handleClose();
      dispatch(getUserById());
    }
  };

  const weeklyIncome = calculateWeeklyIncome(
    rate,
    desiredClients,
    sessionLength
  );
  const annualIncome = calculateAnnualIncome(
    rate,
    desiredClients,
    sessionLength
  );

  return (
    <CustomModal
      open={open}
      handleClose={handleClose}
      title={MODAL_TITLE}
      saveBtnText={SAVE_BTN_TEXT}
      cancelBtnText={CANCEL_BTN_TEXT}
      onSave={handleSave}
      onCancel={scrollToResult}
      width={isMediumScreen ? "75%" : "55%"}
    >
      <ContentBox gap={15}>
        <Subtitle>{SUBTITLE}</Subtitle>

        <FormControlLabel
          control={
            <CheckboxIcon
              checked={totalIncomeToggle}
              onChange={() => setTotalIncomeToggle((prev) => !prev)}
              icon={<TaskAltOutlined sx={checkboxIconSx} />}
              checkedIcon={<TaskAltOutlined sx={checkboxCheckedIconSx} />}
            />
          }
          label={<ToggleLabel>{TOGGLE_LABEL}</ToggleLabel>}
        />

        <CustomSlider
          label={SLIDER_LABELS.CLIENTS}
          label2={SLIDER_LABELS.CLIENTS_HINT}
          max={SLIDER_CONFIG.CLIENTS_MAX}
          value={desiredClients}
          setValue={setDesiredClients}
        />

        <CustomSlider
          label={SLIDER_LABELS.SESSION_LENGTH}
          label2={SLIDER_LABELS.SESSION_LENGTH_HINT}
          max={SLIDER_CONFIG.SESSION_LENGTH_MAX}
          value={sessionLength}
          setValue={setSessionLength}
          mt={4}
        />

        <GridContainer>
          <InputWrapper>
            <CustomInput
              label={INPUT_LABELS.RATE}
              label2={INPUT_LABELS.RATE_HINT}
              placeholder={INPUT_LABELS.RATE_PLACEHOLDER}
              value={rate}
              setValue={setRate}
              noIcon
              smallBr
              limit
              min={RATE_INPUT_CONFIG.MIN}
              max={RATE_INPUT_CONFIG.MAX}
              bR="14px"
              type="number"
              width="100%"
              color={colors.primary}
            />
          </InputWrapper>
          <InputWrapper>
            <DateLabel>{INPUT_LABELS.DATE}</DateLabel>
            <DateHint>{INPUT_LABELS.DATE_HINT}</DateHint>
            <CustomDatePicker value={startDate} setValue={setStartDate} />
          </InputWrapper>
        </GridContainer>

        <DisclaimerStack direction="row" alignItems="center" spacing={1}>
          <InfoOutlined sx={{ color: colors.primary }} />
          <DisclaimerText>{DISCLAIMER_TEXT}</DisclaimerText>
        </DisclaimerStack>

        <ResultBox ref={resultRef}>
          <ResultLabel>{RESULT_LABELS.WEEKLY}</ResultLabel>
          <ResultAmount>
            $
            <ResultValue component="span">
              {weeklyIncome}
              <ResultSuffix component="span">
                {RESULT_LABELS.WEEKLY_SUFFIX}
              </ResultSuffix>
            </ResultValue>
          </ResultAmount>
        </ResultBox>

        <ResultBox>
          <ResultLabel>{RESULT_LABELS.ANNUAL}</ResultLabel>
          <ResultAmount>
            $
            <ResultValue component="span">
              {annualIncome}
              <ResultSuffix component="span">
                {RESULT_LABELS.ANNUAL_SUFFIX}
              </ResultSuffix>
            </ResultValue>
          </ResultAmount>
        </ResultBox>
      </ContentBox>
    </CustomModal>
  );
};

export default CalculatorModal;
